import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import Skeleton from 'core/components/Skeleton';
import ToolTip from 'core/components/Tooltip';
import {
  AlertVariant,
} from 'pages/Dashboard/components/Alert';
import {
  useNotify,
} from 'core/hooks';
import {
  useGetClaimSummaryByClaimIds,
  useMarkClaimStatusBulk,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  validateClaimsByTargetStatus,
} from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/utils';
import StatusSelect from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/StatusSelect';
import ClaimPreview from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/ClaimPreview';
import {
  notEmptyString,
  pluralize,
} from '@populateui/utils';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import clsx from 'clsx';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';

const COMMENT_MAX_LENGTH = 128;

export type BulkClaimStatusDialogProps = {
  claimIds: number[];
};

type Props = BulkClaimStatusDialogProps & {
  onClose: () => void;
};

export default function BulkClaimStatusDialog({ onClose, claimIds }: Props) {
  const {
    data: claimSummaries,
    isLoading: isLoadingClaimSummaries,
  } = useGetClaimSummaryByClaimIds(claimIds);
  const {
    mutateAsync: markClaimStatusBulk,
    isPending: isMarkingClaimStatusBulk,
  } = useMarkClaimStatusBulk();
  const notify = useNotify();

  const [targetStatus, setTargetStatus] = useState<string | null>(null);
  const [removedClaimIds, setRemovedClaimIds] = useState<number[]>([]);
  const [showIneligible, setShowIneligible] = useState(false);
  const [comment, setComment] = useState('');

  const claimsList = useMemo(() => claimSummaries ?? [], [claimSummaries]);

  const { eligibleClaims, ineligibleClaims } = useMemo(
    () => validateClaimsByTargetStatus(claimsList, targetStatus ?? ''),
    [claimsList, targetStatus],
  );

  const claimsToUpdate = useMemo(() => {
    const removedIds = new Set(removedClaimIds);
    return eligibleClaims.filter((claim) => !removedIds.has(claim.claimId ?? 0));
  }, [eligibleClaims, removedClaimIds]);

  const ineligibleStatuses = useMemo(
    () => uniq(ineligibleClaims.map((claim) => claim.status)).join(', '),
    [ineligibleClaims],
  );

  // A new status selection always starts with a fresh eligible list
  const handleStatusChange = useCallback((status: string) => {
    setTargetStatus(status);
    setRemovedClaimIds([]);
    setShowIneligible(false);
  }, []);

  const handleRemove = useCallback((claimId: number) => {
    setRemovedClaimIds((prev) => [...prev, claimId]);
  }, []);

  const updateClaimStatuses = useCallback(async () => {
    try {
      const result = await markClaimStatusBulk({
        claimIds: claimsToUpdate.map((claim) => claim.claimId ?? 0),
        targetStatus,
        comment: notEmptyString(comment) ? comment : null,
      });

      let variant: AlertVariant;
      if ((result.successCount ?? 0) === 0) {
        variant = 'error';
      } else if ((result.failCount ?? 0) > 0) {
        variant = 'warning';
      } else {
        variant = 'success';
      }

      notify({
        message: result.message ?? 'Claim statuses updated.',
        variant,
      });

      const firstClaim = head(claimsToUpdate);
      billingEventChannel.emit('onChargesInfoChange', {
        encounterId: firstClaim?.encounterId ?? 0,
        id: firstClaim?.claimId ?? 0,
      });
      billingEventChannel.emit('onClaimLogChange', firstClaim?.claimId ?? 0);
      billingEventChannel.emit('onBulkClaimStatusUpdated');
      onClose();
    } catch (error) {
      // react query will show error
    }
  }, [claimsToUpdate, targetStatus, comment, markClaimStatusBulk, notify, onClose]);

  return (
    <Dialog
      open
      onClose={onClose}
      onConfirm={updateClaimStatuses}
      title={(
        <div className="flex flex-col">
          <h2 className="text-lg font-semibold mb-0">Bulk Claim Status Update</h2>
          {isLoadingClaimSummaries ? (
            <Skeleton className="!h-4 w-1/2" />
          ) : (
            <p className="text-xs text-gray-600 font-normal">
              {`${pluralize('claim', claimsList.length)} selected`}
            </p>
          )}
        </div>
      )}
      disableBackdropClick={false}
      className="!w-[56rem] overflow-y-hidden !p-0 min-h-[28rem] !space-y-0"
      maxWidth="lg"
      titleClassName="p-4"
    >
      <div className="flex flex-1 flex-col gap-form-row w-full min-w-0 min-h-0 px-5 overflow-y-auto max-h-[calc(90vh-10rem)]">
        {isLoadingClaimSummaries ? (
          <Skeleton className="!h-10 w-80" />
        ) : (
          <StatusSelect
            claimsList={claimsList}
            targetStatus={targetStatus}
            setTargetStatus={handleStatusChange}
          />
        )}
        {!isNil(targetStatus) && (
          <>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center h-8">
                <span className="text-xs font-semibold text-gray-700">Ready to update</span>
                {removedClaimIds.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>{`${removedClaimIds.length} removed ·`}</span>
                    <Button
                      variant="text"
                      size="small"
                      color="primary"
                      className="!text-xs !font-semibold !min-w-0"
                      onClick={() => setRemovedClaimIds([])}
                    >
                      Undo
                    </Button>
                  </div>
                )}
              </div>
              <ClaimPreview
                claims={claimsToUpdate}
                onRemove={handleRemove}
                emptySlot={`No claims left to update to ${targetStatus}.`}
                className="max-h-[22rem]"
              />
            </div>
            {ineligibleClaims.length > 0 && (
              <div className="flex flex-col gap-2">
                <Button
                  variant="text"
                  size="small"
                  color="secondary"
                  aria-expanded={showIneligible}
                  onClick={() => setShowIneligible((prev) => !prev)}
                  className="!self-start !normal-case !text-xs !text-gray-500 !font-medium !px-1"
                  startIcon={(
                    <KeyboardArrowRightIcon
                      className={clsx('!w-4 !h-4 transition-transform duration-150', {
                        'rotate-90': showIneligible,
                      })}
                    />
                  )}
                >
                  {`${pluralize('other claim', ineligibleClaims.length)} can't change to ${targetStatus} (${ineligibleStatuses})`}
                </Button>
                {showIneligible && (
                  <ClaimPreview
                    claims={ineligibleClaims}
                    muted
                    className="max-h-[12.5rem]"
                  />
                )}
              </div>
            )}
            <TextAreaWithActions
              id="bulk-mark-status-comment"
              placeholder="Add comment"
              rows={3}
              value={comment}
              onChange={setComment}
              maxLength={COMMENT_MAX_LENGTH}
              trackingLabel="Bulk Mark Status Comment"
            />
          </>
        )}
      </div>
      <div className="flex justify-between items-center p-5">
        <Button variant="outlined" type="button" onClick={onClose}>
          Close
        </Button>
        <ToolTip
          title={isNil(targetStatus) ? 'Select a target status to continue.' : 'No claims to update.'}
          hide={!isNil(targetStatus) && claimsToUpdate.length > 0}
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={updateClaimStatuses}
              disabled={isNil(targetStatus)
                || claimsToUpdate.length === 0
                || isMarkingClaimStatusBulk}
            >
              {isNil(targetStatus) ? 'Update' : `Update ${pluralize('claim', claimsToUpdate.length)}`}
            </Button>
          </span>
        </ToolTip>
      </div>
    </Dialog>
  );
}
